import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaUpload } from 'react-icons/fa';

function AdminProducts({ products, onUpdate }) {
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    composition: '',
    weight: '',
    description: '',
    category: 'originalSeries',
    stock: 0,
    images: []
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const uploadImages = async () => {
    const IMGBB_API_KEY = '7605882431dbf5299b95fccb3e00b63c'; // Замените на свой ключ с imgbb.com
    const uploadedUrls = [];
    
    for (const file of imageFiles) {
      const formData = new FormData();
      formData.append('image', file);
      
      try {
        const response = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
          method: 'POST',
          body: formData
        });
        
        const data = await response.json();
        if (data.success) {
          uploadedUrls.push(data.data.url);
        } else {
          throw new Error('Ошибка загрузки изображения');
        }
      } catch (error) {
        console.error('Ошибка загрузки:', error);
        throw error;
      }
    }
    return uploadedUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let finalImages = [...(formData.images || [])];
      
      if (imageFiles.length > 0) {
        const newImages = await uploadImages();
        finalImages = [...finalImages, ...newImages];
      }
      
      const dataToSave = {
        name: formData.name,
        price: formData.price,
        composition: formData.composition,
        weight: formData.weight,
        description: formData.description,
        category: formData.category,
        stock: formData.stock,
        images: finalImages
      };
      
      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), dataToSave);
        alert('Товар обновлен');
      } else {
        await addDoc(collection(db, 'products'), dataToSave);
        alert('Товар добавлен');
      }
      resetForm();
      onUpdate();
    } catch (error) {
      alert('Ошибка: ' + error.message);
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      price: product.price || '',
      composition: product.composition || '',
      weight: product.weight || '',
      description: product.description || '',
      category: product.category || 'originalSeries',
      stock: product.stock || 0,
      images: product.images || []
    });
    setImageFiles([]);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить товар?')) {
      await deleteDoc(doc(db, 'products', id));
      onUpdate();
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setShowForm(false);
    setImageFiles([]);
    setFormData({
      name: '',
      price: '',
      composition: '',
      weight: '',
      description: '',
      category: 'originalSeries',
      stock: 0,
      images: []
    });
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + imageFiles.length + formData.images.length > 10) {
      alert('Максимум 10 фотографий');
      return;
    }
    
    // Проверка размера файлов (макс 32MB на ImgBB)
    for (const file of files) {
      if (file.size > 32 * 1024 * 1024) {
        alert(`Файл ${file.name} слишком большой. Максимум 32MB`);
        return;
      }
    }
    
    setImageFiles(prev => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const removeImageFile = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="admin-products">
      <div className="page-header">
        <h2>Управление товарами</h2>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <FaPlus /> Добавить товар
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Поиск товаров..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
          <option value="all">Все категории</option>
          <option value="monoSets">Моно наборы</option>
          <option value="originalSeries">Оригинальная серия</option>
          <option value="farEastSeries">Дальневосточная серия</option>
        </select>
      </div>

      <div className="products-table">
        <table>
          <thead>
            <tr>
              <th>Изображение</th>
              <th>Название</th>
              <th>Категория</th>
              <th>Цена</th>
              <th>Остаток</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(product => (
              <tr key={product.id}>
                <td>
                  {product.images && product.images.length > 0 ? (
                    <img src={product.images[0]} alt={product.name} className="product-thumb" />
                  ) : (
                    <div className="product-thumb-placeholder">Нет фото</div>
                  )}
                </td>
                <td>
                  <strong>{product.name}</strong>
                  {product.composition && <div className="product-meta">{product.composition}</div>}
                </td>
                <td>
                  <span className="category-badge">
                    {product.category === 'monoSets' ? 'Моно' : product.category === 'originalSeries' ? 'Оригинал' : 'Дальний Восток'}
                  </span>
                </td>
                <td><strong>{product.price}</strong></td>
                <td>
                  <span className={`stock-indicator ${product.stock === 0 ? 'stock-zero' : product.stock < 10 ? 'stock-low' : 'stock-ok'}`}>
                    {product.stock} шт.
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon btn-edit" onClick={() => handleEdit(product)}>
                      <FaEdit />
                    </button>
                    <button className="btn-icon btn-delete" onClick={() => handleDelete(product.id)}>
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingProduct ? 'Редактировать товар' : 'Добавить товар'}</h3>
              <button className="btn-icon" onClick={resetForm}><FaTimes /></button>
            </div>
            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Название *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Цена *</label>
                  <input
                    type="text"
                    placeholder="170₽"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Категория *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option value="monoSets">Моно наборы</option>
                    <option value="originalSeries">Оригинальная серия</option>
                    <option value="farEastSeries">Дальневосточная серия</option>
                    <option value="giftSets">Подарочная серия</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Вес</label>
                  <input
                    type="text"
                    placeholder="100 грамм"
                    value={formData.weight}
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Состав</label>
                <input
                  type="text"
                  placeholder="кедровый орех, шиповник, гвоздика..."
                  value={formData.composition}
                  onChange={(e) => setFormData({...formData, composition: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Описание</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  placeholder="Подробное описание товара..."
                />
              </div>

              <div className="form-group">
                <label>Количество в наличии *</label>
                <input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({...formData, stock: parseInt(e.target.value) || 0})}
                  required
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Фотографии товара</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  style={{display: 'none'}}
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="upload-btn">
                  <FaUpload /> Выбрать фото
                </label>
                
                <div className="images-preview">
                  {formData.images.map((url, index) => (
                    <div key={index} className="image-preview-item">
                      <img src={url} alt="" />
                      <button type="button" onClick={() => removeImage(index)} className="remove-image-btn">
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  {imageFiles.map((file, index) => (
                    <div key={`new-${index}`} className="image-preview-item">
                      <img src={URL.createObjectURL(file)} alt="" />
                      <button type="button" onClick={() => removeImageFile(index)} className="remove-image-btn">
                        <FaTimes />
                      </button>
                      <span className="new-badge">Новое</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Отмена
                </button>
                <button type="submit" className="btn-primary" disabled={uploading}>
                  {uploading ? 'Загрузка...' : editingProduct ? 'Сохранить' : 'Добавить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminProducts;
